import { useVirtualizer } from "@tanstack/react-virtual"
import { HTMLAttributes, ReactNode, useRef, useCallback } from "react"
import { cn } from "~/utils/helpers"

interface VirtualizedListProps<T> extends HTMLAttributes<HTMLDivElement> {
  height: number
  estimateSize: number
  items: T[]
  renderItem: (item: T, index: number) => ReactNode
  keyExtractor?: (item: T, index: number) => string | number
}

function VirtualizedList<T>({
  height,
  items: data,
  estimateSize,
  renderItem,
  keyExtractor,
  className,
}: VirtualizedListProps<T>) {
  const parentRef = useRef<HTMLDivElement>(null)
  const getEstimateSize = useCallback(() => estimateSize, [estimateSize])

  const virtualizer = useVirtualizer({
    count: data.length,
    getScrollElement: () => parentRef.current,
    estimateSize: getEstimateSize,
  })

  const virtualItems = virtualizer.getVirtualItems()

  return (
    <div
      ref={parentRef}
      className={cn("List", className)}
      style={{
        height: height,
        width: "100%",
        overflowY: "auto",
        contain: "strict",
      }}
    >
      <div
        style={{
          height: virtualizer.getTotalSize(),
          width: "100%",
          position: "relative",
        }}
      >
        <div
          style={{
            position: "absolute",
            top: 0,
            left: 0,
            width: "100%",
            transform: `translateY(${virtualItems[0]?.start ?? 0}px)`,
          }}
        >
          {virtualItems.map((virtualRow) => {
            const item = data[virtualRow.index]!
            const key = keyExtractor ? keyExtractor(item, virtualRow.index) : virtualRow.index
            return (
              <div key={key} data-index={virtualRow.index} ref={virtualizer.measureElement}>
                {renderItem(item, virtualRow.index)}
              </div>
            )
          })}
        </div>
      </div>
    </div>
  )
}

export default VirtualizedList
